import { graphql } from "@/shared/gql";
import { useQuery } from "@apollo/client";
import { useCompanyId } from "@/entities/company/hooks/use-company-id";
import {
  FetchCompanyTasksQueryVariables,
  AllTaskPropsFragment,
} from "@/shared/gql/graphql";
import { EntityNotFoundError } from "@/shared/lib/errors/404-not-found-error";
import { getPreloadQuery } from "@/shared/gql/apollo-client";
import { PaginationState } from "@/shared/hooks/use-pagination";

export const FetchCompanyTasks = graphql(`
  query FetchCompanyTasks($companyId: ID!, $input: SearchTasksInput!) {
    currentUser(company: $companyId) {
      id
      company {
        id
        tasks(input: $input) {
          data {
            id
            ...AllTaskProps
          }
          paginatorInfo {
            total
          }
        }
      }
    }
  }
`);

// A stable reference is important for tanstack table
const fallback: AllTaskPropsFragment[] = [];

type Props = {
  searchQuery?: string;
  assignees?: string[];
  labels?: string[];
  completed?: boolean;
  pagination: PaginationState;
};

export const useFetchCompanyTasksQuery = ({
  searchQuery,
  labels,
  assignees,
  completed,
  pagination,
}: Props) => {
  const companyId = useCompanyId();

  const { data, loading, error, refetch } = useQuery(FetchCompanyTasks, {
    variables: {
      companyId,
      input: {
        assignees,
        labels,
        searchQuery,
        completed,
        paginator: {
          take: pagination.perPage,
          page: pagination.page,
        },
      },
    },
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return {
      tasks: fallback,
      total: 0,
      isLoading: loading,
      refetch,
    };
  }

  if (data && !data.currentUser.company) {
    throw new EntityNotFoundError("Company");
  }

  const tasksData = data?.currentUser?.company?.tasks;
  const tasks = tasksData?.data || fallback;
  const total = tasksData?.paginatorInfo?.total || 0;

  return {
    tasks,
    total,
    isLoading: loading,
    refetch,
  };
};
export const preloadTasksQuery = (vars: FetchCompanyTasksQueryVariables) => {
  getPreloadQuery()(FetchCompanyTasks, {
    variables: vars,
  });
};
